import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authAPI = {
  googleSession: (sessionId) => apiClient.post('/auth/google-session', { session_id: sessionId }),
  me: () => apiClient.get('/auth/me'),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => apiClient.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyEmail: (token) => apiClient.post('/auth/verify-email', { token }),
  resendVerification: () => apiClient.post('/auth/resend-verification'),
};

export const schoolAPI = {
  create: (data) => apiClient.post('/schools', data),
  get: (schoolId) => apiClient.get(`/schools/${schoolId}`),
  update: (schoolId, data) => apiClient.put(`/schools/${schoolId}`, data),
  uploadLogo: (schoolId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/schools/${schoolId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const userAPI = {
  getProfile: () => apiClient.get('/users/profile'),
  updateProfile: (data) => apiClient.put('/users/profile', data),
  uploadPicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/users/profile/picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const lessonPlanAPI = {
  create: (data) => apiClient.post('/lesson-plans', data),
  getAll: (params) => apiClient.get('/lesson-plans', { params }),
  getOne: (id) => apiClient.get(`/lesson-plans/${id}`),
  update: (id, data) => apiClient.put(`/lesson-plans/${id}`, data),
  delete: (id) => apiClient.delete(`/lesson-plans/${id}`),
  duplicate: (id) => apiClient.post(`/lesson-plans/${id}/duplicate`),
  share: (id, data) => apiClient.post(`/lesson-plans/${id}/share`, data),
  library: () => apiClient.get('/lesson-plans/library/all'),
  bulkImport: (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return apiClient.post('/lesson-plans/admin/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listVersions: (id) => apiClient.get(`/lesson-plans/${id}/versions`),
  restoreVersion: (id, versionNumber) => apiClient.post(`/lesson-plans/${id}/versions/${versionNumber}/restore`),
  createShareLink: (id) => apiClient.post(`/lesson-plans/${id}/share-link`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/lesson-plans/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const templateAPI = {
  getAll: () => apiClient.get('/templates'),
  getOne: (id) => apiClient.get(`/templates/${id}`),
  create: (data) => apiClient.post('/templates', data),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/templates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const documentAPI = {
  generateDOCX: (id, orientation = 'landscape', signingAuthorities = []) => {
    const params = new URLSearchParams({ orientation });
    if (signingAuthorities.length) params.append('signing_authorities', signingAuthorities.join(','));
    return apiClient.post(`/documents/generate/docx/${id}?${params.toString()}`, {}, { responseType: 'blob' });
  },
  generatePDF: (id, orientation = 'landscape', signingAuthorities = []) => {
    const params = new URLSearchParams({ orientation });
    if (signingAuthorities.length) params.append('signing_authorities', signingAuthorities.join(','));
    return apiClient.post(`/documents/generate/pdf/${id}?${params.toString()}`, {}, { responseType: 'blob' });
  },
  inlinePdfUrl: (id, orientation = 'landscape') =>
    `${apiClient.defaults.baseURL}/documents/generate/pdf/${id}/inline?orientation=${orientation}`,
};

export const aiAPI = {
  getSuggestions: (data) => apiClient.post('/ai/suggestions', data, { responseType: 'stream' }),
  autoGenerate: (data) => apiClient.post('/ai/auto-generate', data),
};

export const analyticsAPI = {
  overview: () => apiClient.get('/analytics/overview'),
};

export const publicAPI = {
  getSharedPlan: (token) => apiClient.get(`/public/plan/${token}`),
};

export const worksheetAPI = {
  questionTypes: () => apiClient.get('/worksheets/question-types'),
  uploadChapter: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post('/worksheets/upload-chapter', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadChapters: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return apiClient.post('/worksheets/upload-chapters', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  generate: (data) => apiClient.post('/worksheets/generate', data),
  create: (data) => apiClient.post('/worksheets', data),
  getAll: () => apiClient.get('/worksheets'),
  getOne: (id) => apiClient.get(`/worksheets/${id}`),
  update: (id, data) => apiClient.put(`/worksheets/${id}`, data),
  delete: (id) => apiClient.delete(`/worksheets/${id}`),
  duplicate: (id) => apiClient.post(`/worksheets/${id}/duplicate`),
  library: () => apiClient.get('/worksheets/library/all'),
  downloadDocx: (id, includeAnswerKey = false) =>
    apiClient.post(`/worksheets/${id}/docx?include_answer_key=${includeAnswerKey}`, {}, { responseType: 'blob' }),
  downloadPdf: (id, includeAnswerKey = false) =>
    apiClient.post(`/worksheets/${id}/pdf?include_answer_key=${includeAnswerKey}`, {}, { responseType: 'blob' }),
  inlinePdfUrl: (id, includeAnswerKey = false) =>
    `${apiClient.defaults.baseURL}/worksheets/${id}/pdf/inline?include_answer_key=${includeAnswerKey}`,
};

export const voiceAPI = {
  compose: (file, language = 'auto') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('language', language);
    return apiClient.post('/voice/compose', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const curriculumAPI = {
  chapters: (className, subject, board = 'NCERT') =>
    apiClient.get('/curriculum/chapters', { params: { class_name: className, subject, board } }),
};

export const curriculumDocsAPI = {
  meta: () => apiClient.get('/curriculum-docs/meta'),
  upload: (file, meta) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v ?? ''));
    return apiClient.post('/curriculum-docs/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: 600 * 1024 * 1024,
      maxBodyLength: 600 * 1024 * 1024,
      // 6 minutes = 360s — aligned with Cloudflare origin timeout
      timeout: 6 * 60 * 1000,
    });
  },
  bulkUpload: (files, meta, onProgress) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v ?? ''));
    return apiClient.post('/curriculum-docs/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: 2000 * 1024 * 1024,
      maxBodyLength: 2000 * 1024 * 1024,
      timeout: 6 * 60 * 1000,
      onUploadProgress: onProgress,
    });
  },
  list: (params = {}) => apiClient.get('/curriculum-docs', { params }),
  update: (id, data) => apiClient.put(`/curriculum-docs/${id}`, data),
  delete: (id) => apiClient.delete(`/curriculum-docs/${id}`),
  sessions: () => apiClient.get('/curriculum-docs/sessions'),
};

// Relational Curriculum Library (Board > Session > Class > Subject > Book > Chapter > Topic)
export const curriculumLibraryAPI = {
  meta: () => apiClient.get('/curriculum-library/meta'),
  // Books
  listBooks: (params = {}) => apiClient.get('/curriculum-library/books', { params }),
  getBook: (id) => apiClient.get(`/curriculum-library/books/${id}`),
  createBook: (data) => apiClient.post('/curriculum-library/books', data),
  updateBook: (id, data) => apiClient.put(`/curriculum-library/books/${id}`, data),
  deleteBook: (id) => apiClient.delete(`/curriculum-library/books/${id}`),
  // Chapters
  listChapters: (bookId) => apiClient.get('/curriculum-library/chapters', { params: { book_id: bookId } }),
  createChapter: (data) => apiClient.post('/curriculum-library/chapters', data),
  updateChapter: (id, data) => apiClient.put(`/curriculum-library/chapters/${id}`, data),
  deleteChapter: (id) => apiClient.delete(`/curriculum-library/chapters/${id}`),
  reorderChapters: (bookId, chapterIds) => apiClient.post('/curriculum-library/chapters/reorder', { chapter_ids: chapterIds }),
  // Topics
  listTopics: (chapterId) => apiClient.get('/curriculum-library/topics', { params: { chapter_id: chapterId } }),
  createTopic: (data) => apiClient.post('/curriculum-library/topics', data),
  updateTopic: (id, data) => apiClient.put(`/curriculum-library/topics/${id}`, data),
  deleteTopic: (id) => apiClient.delete(`/curriculum-library/topics/${id}`),
  reorderTopics: (chapterId, topicIds) => apiClient.post('/curriculum-library/topics/reorder', { topic_ids: topicIds }),
  // Tree
  tree: (params = {}) => apiClient.get('/curriculum-library/tree', { params }),
  // Upload + Extract
  uploadExtract: (bookId, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post(`/curriculum-library/books/${bookId}/upload-extract`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: 600 * 1024 * 1024,
      maxBodyLength: 600 * 1024 * 1024,
      timeout: 6 * 60 * 1000,
      onUploadProgress: onProgress,
    });
  },
  approveExtraction: (bookId, chapters) => apiClient.post(`/curriculum-library/books/${bookId}/approve-extraction`, { book_id: bookId, chapters }),
  uploadFolder: (files, meta, onProgress) => {
    const fd = new FormData();
    files.forEach((f) => {
      fd.append('files', f);
      fd.append('relative_paths', f.webkitRelativePath || f.name);
    });
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v ?? ''));
    return apiClient.post('/curriculum-library/upload-folder', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: 2000 * 1024 * 1024,
      maxBodyLength: 2000 * 1024 * 1024,
      timeout: 8 * 60 * 1000,
      onUploadProgress: onProgress,
    });
  },
  bulkChapters: (bookId, data) => apiClient.post(`/curriculum-library/books/${bookId}/bulk-chapters`, data),
  // Chapter files
  attachFile: (chapterId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post(`/curriculum-library/chapters/${chapterId}/attach-file`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 6 * 60 * 1000,
    });
  },
  listFiles: (chapterId) => apiClient.get(`/curriculum-library/chapters/${chapterId}/files`),
  deleteFile: (fileId) => apiClient.delete(`/curriculum-library/chapter-files/${fileId}`),
  // Migration
  migrate: () => apiClient.post('/curriculum-library/migrate-from-legacy'),
};

// Sample Paper generator — reuses question_paper backend under a distinct UI wrapper
export const samplePaperAPI = {
  patterns: () => apiClient.get('/question-papers/patterns'),
  generate: (data) => apiClient.post('/question-papers/generate', { ...data, exam_type: 'Sample Paper' }),
  save: (data) => apiClient.post('/question-papers', { ...data, exam_type: 'Sample Paper' }),
  getOne: (id) => apiClient.get(`/question-papers/${id}`),
  update: (id, data) => apiClient.put(`/question-papers/${id}`, data),
  downloadDocx: (id, includeAnswerKey = false) =>
    apiClient.post(`/question-papers/${id}/docx?include_answer_key=${includeAnswerKey}`, {}, { responseType: 'blob' }),
  downloadPdf: (id, includeAnswerKey = false) =>
    apiClient.post(`/question-papers/${id}/pdf?include_answer_key=${includeAnswerKey}`, {}, { responseType: 'blob' }),
  inlinePdfUrl: (id, includeAnswerKey = false) =>
    `${apiClient.defaults.baseURL}/question-papers/${id}/pdf/inline?include_answer_key=${includeAnswerKey}`,
};

export const questionPaperAPI = {
  patterns: () => apiClient.get('/question-papers/patterns'),
  pattern: (key) => apiClient.get(`/question-papers/pattern/${encodeURIComponent(key)}`),
  suggest: (className, subject) => apiClient.get('/question-papers/suggest', { params: { class_name: className, subject } }),
  uploadChapter: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post('/question-papers/upload-chapter', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  generate: (data) => apiClient.post('/question-papers/generate', data),
  create: (data) => apiClient.post('/question-papers', data),
  getAll: () => apiClient.get('/question-papers'),
  getOne: (id) => apiClient.get(`/question-papers/${id}`),
  update: (id, data) => apiClient.put(`/question-papers/${id}`, data),
  delete: (id) => apiClient.delete(`/question-papers/${id}`),
  duplicate: (id) => apiClient.post(`/question-papers/${id}/duplicate`),
  library: () => apiClient.get('/question-papers/library/all'),
  downloadDocx: (id, includeAnswerKey = false) =>
    apiClient.post(`/question-papers/${id}/docx?include_answer_key=${includeAnswerKey}`, {}, { responseType: 'blob' }),
  downloadPdf: (id, includeAnswerKey = false) =>
    apiClient.post(`/question-papers/${id}/pdf?include_answer_key=${includeAnswerKey}`, {}, { responseType: 'blob' }),
  inlinePdfUrl: (id, includeAnswerKey = false) =>
    `${apiClient.defaults.baseURL}/question-papers/${id}/pdf/inline?include_answer_key=${includeAnswerKey}`,
  // NEW
  bulkUpload: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return apiClient.post('/question-papers/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  translate: (id, target = 'hi') =>
    apiClient.post(`/question-papers/${id}/translate?target=${target}`),
  myPatterns: () => apiClient.get('/question-papers/my-patterns'),
  createMyPattern: (data) => apiClient.post('/question-papers/my-patterns', data),
  updateMyPattern: (pid, data) => apiClient.put(`/question-papers/my-patterns/${pid}`, data),
  deleteMyPattern: (pid) => apiClient.delete(`/question-papers/my-patterns/${pid}`),
};

// Question Paper Formats — admin templates + teacher-uploaded PDF/DOCX blueprints
export const qpFormatsAPI = {
  list: (params = {}) => apiClient.get('/qp-formats', { params }),
  get: (id) => apiClient.get(`/qp-formats/${id}`),
  upload: (file, meta, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, String(v ?? '')));
    return apiClient.post('/qp-formats/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: 60 * 1024 * 1024,
      maxBodyLength: 60 * 1024 * 1024,
      timeout: 6 * 60 * 1000,
      onUploadProgress: onProgress,
    });
  },
  create: (data) => apiClient.post('/qp-formats', data),
  update: (id, data) => apiClient.put(`/qp-formats/${id}`, data),
  delete: (id) => apiClient.delete(`/qp-formats/${id}`),
};

export default apiClient;